import { css } from 'astroturf';
import React from 'react';

import Link from '../link';

const styles = css`
  .featurestrip {
    padding: 2rem 1rem;
    max-width: 1200px;
    margin: 5rem auto 5rem;
  }

  .featurestrip h2 {
    text-align: center;
    font-size: 1.5rem;
    line-height: 1;
    margin-bottom: 2rem;
  }

  .features {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 0;
  }

  .feature {
    position: relative;
    text-align: center;
    flex: 1 1 160px;
    padding: 0 12px;

    & + .feature::before {
      content: '';
      position: absolute;
      top: 25%;
      left: 0;
      height: 50%;
      width: 1px;
      background: #e4e4e7;
    }
  }

  .icon {
    width: 52px;
    height: 52px;
    margin: 0 auto 6px;
    display: grid;
    place-items: center;
  }

  .icon svg {
    width: 52px;
    height: 52px;
    stroke-width: 1.5;
    stroke: url(#icon-gradient) #1583d8;
  }

  .feature h3 {
    margin-bottom: 10px;
    font-size: 1rem;
    line-height: 1.2;
  }

  .feature p {
    color: #667085;
    font-size: 0.875rem;
    line-height: 1.5;
    text-wrap: pretty;
  }

  .linkcontainer {
    color: #667085;
    text-align: center;
    margin: 2rem 0 0rem;
    font-size: 0.875rem;
  }
  .link {
    color: rgb(0, 105, 161);
    background: rgb(16 125 181 / 8%);
    border-radius: 20px;
    padding: 6px 12px;
    font-size: 1em;
    line-height: 1;
    font-weight: 500;
    text-align: center;
    display: inline-block;
    align-self: start;
    white-space: nowrap;
    margin-left: 1rem;
    transition: all 0.2s ease-in-out;

    &:hover,
    &:focus-visible {
      color: rgb(0, 161, 105);
      background: rgb(16 181 125 / 8%);
    }
  }
`;

const Features = () => {
  const noop = () => {};
  return (
    <section className={styles.featurestrip}>
      <svg style={{ width: 0, height: 0, position: 'absolute' }} aria-hidden="true" focusable="false">
        <linearGradient id="icon-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1583d8" />
          <stop offset="100%" stopColor="#15d883" />
        </linearGradient>
      </svg>

      <h2>Every plan includes all features</h2>

      <div className={styles.features}>
        <article className={styles.feature}>
          <div className={styles.icon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-devices"
            >
              <path d="M13 9a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-6a1 1 0 0 1 -1 -1v-10" />
              <path d="M18 8v-3a1 1 0 0 0 -1 -1h-13a1 1 0 0 0 -1 1v12a1 1 0 0 0 1 1h9" />
              <path d="M16 9h2" />
            </svg>
          </div>

          <h3>Unlimited viewports</h3>

          <p>Test across multiple viewports in real time.</p>
        </article>

        <article className={styles.feature}>
          <div className={`${styles.icon}`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-accessible"
            >
              <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
              <path d="M10 16.5l2 -3l2 3m-2 -3v-2l3 -1m-6 0l3 1" />
              <path d="M11.5 7.5a.5 .5 0 1 0 1 0a.5 .5 0 1 0 -1 0" fill="currentColor" />
            </svg>
          </div>

          <h3>Accessibility auditing</h3>

          <p>Catch issues early with automated and manual audits.</p>
        </article>

        <article className={styles.feature}>
          <div className={styles.icon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-camera"
            >
              <path d="M5 7h1a2 2 0 0 0 2 -2a1 1 0 0 1 1 -1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-9a2 2 0 0 1 2 -2" />
              <path d="M9 13a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
            </svg>
          </div>

          <h3>Screenshots &amp; video</h3>

          <p>Create, annotate and share screenshots and video in one click.</p>
        </article>

        <article className={styles.feature}>
          <div className={`${styles.icon}`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-device-mobile"
            >
              <path d="M6 5a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2v-14" />
              <path d="M11 4h2" />
              <path d="M12 17v.01" />
            </svg>
          </div>

          <h3>Device emulation</h3>

          <p>Realistic emulation of devices, browsers and network conditions.</p>
        </article>

        <article className={styles.feature}>
          <div className={`${styles.icon}`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-reload"
            >
              <path d="M19.933 13.041a8 8 0 1 1 -9.925 -8.788c3.899 -1 7.935 1.007 9.425 4.747" />
              <path d="M20 4v5h-5" />
            </svg>
          </div>

          <h3>Live reload</h3>

          <p>See changes instantly and debug like a pro.</p>
        </article>

        <article className={styles.feature}>
          <div className={styles.icon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-device-imac-search"
            >
              <path d="M11 17h-7a1 1 0 0 1 -1 -1v-12a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v8" />
              <path d="M3 13h10" />
              <path d="M8 21h4" />
              <path d="M10 17l-.5 4" />
              <path d="M15 18a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
              <path d="M20.2 20.2l1.8 1.8" />
            </svg>
          </div>

          <h3>SEO &amp; quality checks</h3>

          <p>Check SEO, performance and best practices.</p>
        </article>
      </div>
      <p className={styles.linkcontainer}>
        Want to learn more first?
        <Link to="/product-tour" className={styles.link}>
          See all features
        </Link>
      </p>
    </section>
  );
};

export default Features;
